/**
 * 전체 페이지 수가 바뀔 때 기존 기록을 새 척도로 옮긴다.
 *
 * 전자책은 글자 크기·배율에 따라 총 쪽수가 달라진다. 같은 책인데 300쪽이던 게
 * 450쪽이 되면, 이미 기록해둔 '오늘 30쪽'이나 'p.45' 는 옛 척도의 값이라
 * 그대로 두면 진행률과 인용 위치가 어긋난다.
 *
 * 반대로 총 쪽수를 처음에 잘못 입력했던 경우라면 기록은 이미 옳으므로
 * 분모만 갈아끼워야 한다. 둘 다 정당해서 호출측이 모드를 고른다.
 */

/**
 * 일별 독서량을 비율만큼 늘리거나 줄인다.
 *
 * 각 값을 그냥 반올림하면 합계가 원본×비율에서 벗어난다(진행률이 튄다).
 * 최대잉여법으로 소수부가 큰 날부터 1쪽씩 배분해 합계를 정확히 맞춘다.
 */
export const scaleReadingHistory = (history, ratio) => {
  const entries = Object.entries(history || {}).filter(([, pages]) => pages > 0);
  if (entries.length === 0 || !Number.isFinite(ratio) || ratio <= 0) return {};

  const sourceTotal = entries.reduce((sum, [, pages]) => sum + pages, 0);
  const targetTotal = Math.round(sourceTotal * ratio);

  const scaled = entries.map(([date, pages]) => {
    const exact = pages * ratio;
    const floor = Math.floor(exact);
    return { date, value: floor, frac: exact - floor };
  });

  let total = scaled.reduce((sum, item) => sum + item.value, 0);

  // floor 때문에 항목당 최대 1쪽이 모자라므로 한 번의 배분으로 충분하다
  const byFraction = [...scaled].sort((a, b) => b.frac - a.frac);
  for (const item of byFraction) {
    if (total >= targetTotal) break;
    item.value += 1;
    total += 1;
  }
  // 반대로 넘쳤다면 큰 값부터 덜어낸다 (0 밑으로는 내리지 않는다)
  const byValue = [...scaled].sort((a, b) => b.value - a.value);
  for (const item of byValue) {
    if (total <= targetTotal) break;
    if (item.value === 0) continue;
    item.value -= 1;
    total -= 1;
  }

  return scaled.reduce((acc, { date, value }) => {
    if (value > 0) acc[date] = value;
    return acc;
  }, {});
};

/** 페이지 '번호'를 새 척도로 (1 미만이나 총 쪽수 초과로는 가지 않게) */
export const scalePageNumber = (page, ratio, newTotalPages) => {
  if (typeof page !== 'number' || page <= 0) return page ?? null;
  const scaled = Math.round(page * ratio);
  return Math.min(Math.max(scaled, 1), newTotalPages);
};

/**
 * 총 쪽수 변경에 따른 책 문서 갱신분을 만든다.
 *
 * @param {Object} book
 * @param {number} newTotalPages
 * @param {'rescale'|'correct'} mode
 *   rescale — 배율이 바뀌어 쪽수 체계 자체가 달라졌다. 기록을 비례 이동한다
 *   correct — 총 쪽수만 잘못 적혀 있었다. 기록은 그대로 두고 분모만 바꾼다
 * @returns {{ changes: Object, ratio: number }}
 */
export const buildTotalPagesChange = (book, newTotalPages, mode) => {
  const oldTotal = book?.totalPages || 0;
  const ratio = oldTotal > 0 ? newTotalPages / oldTotal : 1;

  if (mode !== 'rescale' || oldTotal === 0 || ratio === 1) {
    // 분모만 교체. 다만 기존 기록이 새 총량을 넘으면 그대로 둘 수 없다
    const read = Object.values(book?.readingHistory || {}).reduce((s, p) => s + p, 0)
      + (book?.initialPage || 0);
    return {
      ratio: 1,
      changes: { totalPages: newTotalPages },
      overflow: read > newTotalPages ? read - newTotalPages : 0
    };
  }

  const changes = {
    totalPages: newTotalPages,
    readingHistory: scaleReadingHistory(book.readingHistory, ratio),
    initialPage: Math.round((book.initialPage || 0) * ratio)
  };

  // 목표일을 다시 잡았을 때의 기준 쪽수도 같은 척도로 옮긴다
  if (book.resetPageCount != null) {
    changes.resetPageCount = Math.round(book.resetPageCount * ratio);
  }

  return { ratio, changes, overflow: 0 };
};
